import { useState, useEffect, useRef } from 'react'
import ReactMarkdown from 'react-markdown'
import {
  sendDebugChatTurn,
  type ChatMessage,
  type AgentTrace,
  type WatchBuildResult,
} from '../api/client'
import { JsonViewer } from './JsonViewer'
import { BuildSummary } from './BuildSummary'
import { WatchLoader } from './WatchLoader'

export function AgentObserver() {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      conversation_id: 'debug-conv',
      sequence: 0,
      role: 'assistant',
      content: 'I am the Agent Observer chat. Talk to me to see exactly how I think and execute tools behind the scenes.',
      raw_message: null,
    },
  ])
  const [draft, setDraft] = useState('')
  const [conversationId, setConversationId] = useState<string | undefined>()
  const [buildResult, setBuildResult] = useState<WatchBuildResult | null>(null)
  const [trace, setTrace] = useState<AgentTrace | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'timeline' | 'reasoning' | 'messages' | 'build'>('timeline')
  const [expandedStepIndex, setExpandedStepIndex] = useState<number | null>(null)

  const chatEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const message = draft.trim()
    if (!message || isLoading) return

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      conversation_id: conversationId || 'debug-conv',
      sequence: messages.length,
      role: 'user',
      content: message,
      raw_message: null,
    }

    setMessages((prev) => [...prev, userMessage])
    setDraft('')
    setIsLoading(true)
    setError(null)

    try {
      const response = await sendDebugChatTurn({
        message,
        conversation_id: conversationId,
      })

      setConversationId(response.conversation_id)
      setBuildResult(response.build_result)
      setTrace(response.agent_trace)
      
      setMessages((prev) => [
        ...prev,
        ...response.messages.filter(
          (newMsg) => !prev.some((oldMsg) => oldMsg.id === newMsg.id)
        ),
      ])

      // Auto focus timeline or build tab depending on what happened
      if (response.build_result) {
        setActiveTab('build')
      } else {
        setActiveTab('timeline')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="debug-page agent-observer">
      <header className="debug-header">
        <h1>Agent Cognitive Observer</h1>
        <p className="subtitle">
          Observe how the AI agent processes natural language, generates intents, executes tools sequentially, evaluates results, and reasons its way to a final build.
        </p>
      </header>

      <div className="observer-split-layout">
        {/* Left Side: Interactive Chat Panel */}
        <section className="observer-chat-panel">
          <div className="panel-title">Interactive Agent Chat</div>
          <div className="chat-messages-container">
            {messages.map((msg, index) => (
              <div key={msg.id || index} className={`observer-msg-bubble ${msg.role}`}>
                <div className="msg-header">
                  <span className="msg-role">{msg.role.toUpperCase()}</span>
                </div>
                <div className="msg-body">
                  <ReactMarkdown>{msg.content}</ReactMarkdown>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="observer-msg-bubble assistant typing">
                <div className="msg-header">
                  <span className="msg-role">AGENT</span>
                </div>
                <div className="msg-body">
                  <WatchLoader
                    variant="dark"
                    size="sm"
                    label="LLM is thinking, validating compatibilities, and selecting parts…"
                  />
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <form className="observer-chat-form" onSubmit={handleSubmit}>
            <input
              type="text"
              placeholder="Ask the agent to build a watch (e.g. 'Build a 38mm classic dress watch with brown leather strap')"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={isLoading}
            />
            <button type="submit" className="btn-primary" disabled={isLoading || !draft.trim()}>
              Send
            </button>
          </form>

          {error && (
            <div className="error-banner small">
              <p>{error}</p>
            </div>
          )}
        </section>

        {/* Right Side: Observatory Details Dashboard */}
        <section className="observer-dashboard-panel">
          <div className="dashboard-tab-bar">
            <button
              className={`tab-btn ${activeTab === 'timeline' ? 'active' : ''}`}
              onClick={() => setActiveTab('timeline')}
            >
              Timeline Tracer
            </button>
            <button
              className={`tab-btn ${activeTab === 'reasoning' ? 'active' : ''}`}
              onClick={() => setActiveTab('reasoning')}
            >
              Cognitive Chain
            </button>
            <button
              className={`tab-btn ${activeTab === 'messages' ? 'active' : ''}`}
              onClick={() => setActiveTab('messages')}
            >
              Raw Messages ({trace?.steps.length || 0})
            </button>
            {buildResult && (
              <button
                className={`tab-btn highlight ${activeTab === 'build' ? 'active' : ''}`}
                onClick={() => setActiveTab('build')}
              >
                Watch Result
              </button>
            )}
          </div>

          <div className="dashboard-tab-content">
            {!trace && !isLoading && (
              <div className="empty-dashboard-state">
                <h3>Observatory Ready</h3>
                <p>Send a message on the left to start tracing the model's tool invocations, search brief structures, and final decisions.</p>
              </div>
            )}

            {isLoading && !trace && (
              <div className="empty-dashboard-state">
                <WatchLoader
                  variant="dark"
                  size="md"
                  label="Collecting cognitive steps, tool calls, and performance metadata…"
                />
                <h3>Tracing turn execution…</h3>
              </div>
            )}

            {trace && activeTab === 'timeline' && (
              <div className="timeline-view">
                <h3>Cognitive Timeline</h3>
                
                {trace.search_brief && (
                  <div className="timeline-node search-brief-node">
                    <div className="node-marker"></div>
                    <div className="node-content card">
                      <h4>Search Brief Initialized</h4>
                      <p className="description-text">NLP engine successfully extracted keywords and bounds prior to LLM execution:</p>
                      <div className="brief-visualizer grid-2">
                        <div>
                          <strong>Global Filters:</strong>
                          <ul>
                            <li>Styles: {((trace.search_brief.global_intent as any)?.style_families || []).join(', ') || 'any'}</li>
                            <li>Colors: {((trace.search_brief.global_intent as any)?.colors || []).join(', ') || 'any'}</li>
                            <li>Movements: {((trace.search_brief.global_intent as any)?.movement_ids || []).join(', ') || 'any'}</li>
                          </ul>
                        </div>
                        <div>
                          <strong>Mechanical Limits:</strong>
                          <ul>
                            <li>Target Diameter: {trace.search_brief.case_diameter ? `${trace.search_brief.case_diameter}mm` : 'unbounded'}</li>
                            <li>Budget Limit: {trace.search_brief.budget_max ? `$${trace.search_brief.budget_max}` : 'unbounded'}</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {trace.steps.map((step, idx) => {
                  const isExpanded = expandedStepIndex === idx
                  let cardTitle = ''
                  let cardClass = ''

                  if (step.step_type === 'reasoning') {
                    cardTitle = 'Model Cognitive Reasoning'
                    cardClass = 'node-reasoning'
                  } else if (step.step_type === 'tool_call') {
                    cardTitle = `Tool Invoked: ${step.tool_name}`
                    cardClass = 'node-tool-call'
                  } else if (step.step_type === 'tool_result') {
                    cardTitle = `Tool Output: ${step.tool_name}`
                    cardClass = 'node-tool-result'
                  }

                  return (
                    <div key={idx} className={`timeline-node ${cardClass}`}>
                      <div className="node-marker"></div>
                      <div className="node-content card" onClick={() => setExpandedStepIndex(isExpanded ? null : idx)}>
                        <div className="node-header">
                          <h4>{cardTitle}</h4>
                          <span className="expand-indicator">{isExpanded ? 'Collapse ▲' : 'Click to Expand ▼'}</span>
                        </div>

                        {step.step_type === 'reasoning' && (
                          <p className="line-clamp-reasoning">
                            {isExpanded ? step.content : `${step.content?.substring(0, 140)}...`}
                          </p>
                        )}

                        {step.step_type === 'tool_call' && (
                          <div className="tool-call-summary">
                            <span>Calling <code>{step.tool_name}</code> with arguments:</span>
                            {isExpanded ? (
                              <JsonViewer data={step.arguments} title="Tool Arguments" />
                            ) : (
                              <pre className="inline-json"><code>{JSON.stringify(step.arguments)}</code></pre>
                            )}
                          </div>
                        )}

                        {step.step_type === 'tool_result' && (
                          <div className="tool-result-summary">
                            <span>Tool returned response:</span>
                            {isExpanded ? (
                              <JsonViewer data={step.result} title="Tool Result Payload" />
                            ) : (
                              <p className="truncated-result-text">
                                {typeof step.result === 'string' 
                                  ? step.result.substring(0, 80)
                                  : Array.isArray(step.result)
                                    ? `Array of ${step.result.length} objects`
                                    : `Object payload: ${JSON.stringify(step.result).substring(0, 80)}...`}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {trace && activeTab === 'reasoning' && (
              <div className="reasoning-chain-view">
                <h3>Cognitive Chain-of-Thought</h3>
                <p className="subtitle">This lists only the reasoning sentences formulated by the LLM in order of execution.</p>
                <div className="reasoning-chain-list">
                  {trace.steps
                    .filter((s) => s.step_type === 'reasoning')
                    .map((step, idx) => (
                      <div key={idx} className="reasoning-step-item card">
                        <div className="step-num">CoT Stage {idx + 1}</div>
                        <p className="cot-text">{step.content}</p>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {trace && activeTab === 'messages' && (
              <div className="messages-history-view">
                <h3>Raw Messages Transcript</h3>
                <p className="subtitle">The structured serialized model messages sent to and received from <code>pydantic-ai</code>.</p>
                <div className="messages-list">
                  {messages.map((msg, index) => (
                    <div key={index} className="message-history-item card">
                      <div className="msg-header">
                        <strong>Role: <code>{msg.role}</code></strong>
                        <span>Seq: {msg.sequence ?? index}</span>
                      </div>
                      <div className="msg-meta-body">
                        <pre className="monospace-code">{msg.content}</pre>
                        {msg.raw_message && (
                          <JsonViewer data={msg.raw_message} title="Raw Pydantic-AI Structure" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {buildResult && activeTab === 'build' && (
              <div className="watch-result-view">
                <h3>Constructed Build Output</h3>
                <p className="subtitle">This build result was returned by the deterministic pipeline execution.</p>
                <div className="parts-overview reset-margin">
                  <BuildSummary buildResult={buildResult} />
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}
